// src/data/leasesClient.js
//
// Real client for /api/locations/:locId/leases. Slice 2b ships exactly
// two write endpoints — addLease (POST) and updateLease (PATCH) —
// matching the routes shipped in server/routes/leases.js. Allowances,
// draws, critical-dates and watch-items are out of scope (slices 3 & 4).
//
// Contract (src/data/CONTRACT.md → "Leases"):
//   - All methods async, return camelCase Lease rows.
//   - Both writes invalidate the entire location cache (full-bust per the
//     locationCache convention) so the next getLocation refetch sees the
//     new lease tree on the parent.
//   - recordAudit() is called with action "lease.create" / "lease.update"
//     and a {before, after} snapshot.
//   - Errors are typed instances from src/data/errors.js.
//   - 409 → ConflictError. The server emits the canonical message
//     "Location already has an active direct lease..." for the partial-unique
//     uq_leases_one_active_direct collision (migration 0033); the LeasePanel
//     surfaces it verbatim.

import {
  AuthError,
  ConflictError,
  NetworkError,
  NotFoundError,
  ValidationError,
  errorFromResponse,
} from "./errors.js";
// Slice 7b: writes fan out through the cache bus so the portfolio
// reports cache (services/reportsCache.js) is busted alongside the
// per-location cache. Same blast-radius rule as locationCache —
// "invalidate everything" is correct for a < 100 site portfolio.
import {
  invalidatePortfolioCaches as invalidateAll,
  emitCommandIndexStale,
} from "../services/cacheBus.js";
import { recordAudit } from "../services/auditLog.js";
import { log } from "../utils/logger.js";
import { LEASE_TYPES } from "../utils/leaseTypeConstants.js";
import { areaFootagePatchError } from "../../shared/leaseAreaValidation.js";

// ─── Constants (mirror server/routes/leases.js) ───────────────────────────

const LOC_ID_RE       = /^(AMER|EMEA|APAC)-[A-Z]{3}-\d{2}$/;
const LEASE_ID_RE     = /^L\.(AMER|EMEA|APAC)-[A-Z]{3}-\d{2}\.\d+$/;
// Server format: ALLOW.<leaseId>.<2-digit seq>  (see server/routes/leases.js#nextAllowanceId)
const ALLOWANCE_ID_RE = /^ALLOW\.L\.(AMER|EMEA|APAC)-[A-Z]{3}-\d{2}\.\d+\.\d{2}$/;
// Server format: DRW.<allowanceId>.<3-digit seq>  (see server/routes/leases.js#nextDrawId)
const DRAW_ID_RE      = /^DRW\.ALLOW\.L\.(AMER|EMEA|APAC)-[A-Z]{3}-\d{2}\.\d+\.\d{2}\.\d{3}$/;

// Server-canonical enum values. Duplicated here so we fail fast on the
// client without a round-trip; if the server set drifts the *.test.js for
// both sides will catch it.
const STATUSES = new Set([
  "active", "holdover", "in_negotiation", "expired", "terminated", "sublease_out",
  "draft", // LSE-LEASE-TAB-DRAFT-CREATION
]);
const TYPES = new Set(LEASE_TYPES);
const ESCALATION_TYPES       = new Set(["Fixed", "CPI", "None"]);
const OPEX_STRUCTURES        = new Set(["NNN", "Modified Gross", "Full Service Gross", "Gross"]);
const RENT_BASES = new Set(["fixed", "zero_sublease", "rent_free", "per_lease_terms", "not_recorded"]);
// LEASE-FIELDS-ADD-DROP: SECURITY_DEPOSIT_TYPES removed (security_deposit_type column dropped in 0216)

const REQUIRED_NEW = ["status", "type", "startDate", "endDate", "currency"];

const ALLOWANCE_TYPES = new Set([
  "ti_allowance", "moving_allowance", "abatement", "test_fit", "other",
]);
const REQUIRED_NEW_ALLOWANCE = ["type", "totalAmount", "currency"];
const REQUIRED_NEW_DRAW      = ["amount", "drawDate"];

// ─── Internal: fetch wrapper that throws typed errors ─────────────────────

async function jsonRequest(url, init) {
  let res;
  try {
    res = await fetch(url, {
      ...init,
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
    });
  } catch (err) {
    log.error("leasesClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("leasesClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }
  if (res.status === 204) return null;
  try {
    return await res.json();
  } catch (err) {
    throw new NetworkError(`Malformed JSON from ${url}`, err);
  }
}

// ─── Validators ───────────────────────────────────────────────────────────

function assertValidLocId(method, locId) {
  if (!locId || typeof locId !== "string" || !LOC_ID_RE.test(locId)) {
    throw new ValidationError(
      `${method}: invalid locationId "${locId}" - must match REGION-CITYCODE-SEQ`,
      { field: "locationId", got: locId }
    );
  }
}

function assertValidLeaseId(method, leaseId) {
  if (!leaseId || typeof leaseId !== "string" || !LEASE_ID_RE.test(leaseId)) {
    throw new ValidationError(
      `${method}: invalid leaseId "${leaseId}" - must match L.<locId>.<seq>`,
      { field: "leaseId", got: leaseId }
    );
  }
}

// Pre-flight enum checks. Mirrors server/routes/leases.js#validateEnumFields
// so we surface a clear ValidationError without paying a round-trip.
function assertEnums(method, body) {
  if (body.status != null && !STATUSES.has(body.status)) {
    throw new ValidationError(`${method}: invalid status "${body.status}"`, { field: "status", got: body.status });
  }
  if (body.type != null && !TYPES.has(body.type)) {
    throw new ValidationError(`${method}: invalid type "${body.type}"`, { field: "type", got: body.type });
  }
  if (body.escalationType != null && body.escalationType !== "" && !ESCALATION_TYPES.has(body.escalationType)) {
    throw new ValidationError(`${method}: invalid escalationType "${body.escalationType}"`, { field: "escalationType", got: body.escalationType });
  }
  if (body.opexStructure != null && body.opexStructure !== "" && !OPEX_STRUCTURES.has(body.opexStructure)) {
    throw new ValidationError(`${method}: invalid opexStructure "${body.opexStructure}"`, { field: "opexStructure", got: body.opexStructure });
  }
  if (body.rentBasis !== undefined && !RENT_BASES.has(body.rentBasis)) {
    throw new ValidationError(`${method}: invalid rentBasis "${body.rentBasis}"`, { field: "rentBasis", got: body.rentBasis });
  }
  // LEASE-FIELDS-ADD-DROP: securityDepositType validation removed (column dropped in 0216)
}

function assertValidAllowanceId(method, allowanceId) {
  if (!allowanceId || typeof allowanceId !== "string" || !ALLOWANCE_ID_RE.test(allowanceId)) {
    throw new ValidationError(
      `${method}: invalid allowanceId "${allowanceId}" - must match ALLOW.<leaseId>.<seq2>`,
      { field: "allowanceId", got: allowanceId }
    );
  }
}

function assertValidDrawId(method, drawId) {
  if (!drawId || typeof drawId !== "string" || !DRAW_ID_RE.test(drawId)) {
    throw new ValidationError(
      `${method}: invalid drawId "${drawId}" - must match DRW.<allowanceId>.<seq3>`,
      { field: "drawId", got: drawId }
    );
  }
}

function assertAllowanceEnums(method, body) {
  if (body.type != null && !ALLOWANCE_TYPES.has(body.type)) {
    throw new ValidationError(
      `${method}: invalid allowance type "${body.type}"`,
      { field: "type", got: body.type }
    );
  }
}

// ─── Reads ────────────────────────────────────────────────────────────────

/**
 * LEASES-FORKLIFT-1-DETAIL-PAGE — fetch lightweight header metadata for a
 * single lease. Used by LeaseDetailPage to show lease ID / type / status /
 * back-link before rendering the full LeaseSummaryTab content.
 *
 * @param {string} leaseId
 * @returns {Promise<{id, locationId, leaseType, status, city, locationAlias}>}
 */
export async function getLeaseById(leaseId) {
  assertValidLeaseId("getLeaseById", leaseId);
  return jsonRequest(`/api/leases/${encodeURIComponent(leaseId)}`);
}

/**
 * INGEST-POST-APPLY-FIXES — fetch unresolved import conflicts for a lease.
 * Returns diffs whose current field source is "manual" and the latest import
 * brought a different value. Keyed by fieldName for O(1) lookup in the UI.
 *
 * @param {string} leaseId
 * @returns {Promise<Record<string, {diffId, incomingValue, importedAt, sessionId}>>}
 */
export async function getLeaseConflicts(leaseId) {
  if (!leaseId) throw new ValidationError("getLeaseConflicts: leaseId required");
  const data = await jsonRequest(`/api/leases/${encodeURIComponent(leaseId)}/conflicts`);
  const map = {};
  for (const c of data.conflicts || []) {
    map[c.fieldName] = {
      diffId:        c.diffId,
      incomingValue: c.incomingValue,
      importedAt:    c.importedAt,
      sessionId:     c.sessionId,
    };
  }
  return map;
}

/**
 * INGEST-POST-APPLY-FIXES — resolve an import conflict for a lease field.
 * action: "keep_manual" | "use_incoming"
 *
 * @param {string} leaseId
 * @param {string} diffId
 * @param {"keep_manual"|"use_incoming"} action
 * @returns {Promise<{ok: boolean, resolution: string}>}
 */
export async function resolveLeaseConflict(leaseId, diffId, action) {
  if (!leaseId) throw new ValidationError("resolveLeaseConflict: leaseId required");
  if (!diffId)  throw new ValidationError("resolveLeaseConflict: diffId required");
  const result = await jsonRequest(
    `/api/leases/${encodeURIComponent(leaseId)}/conflicts/${encodeURIComponent(diffId)}/resolve`,
    { method: "POST", body: JSON.stringify({ action }) }
  );
  // If the incoming value was applied, bust caches so the lease data refetches.
  if (action === "use_incoming") invalidateAll();
  return result;
}

/**
 * LSE6 — fetch the single active lease for a location with rolled-up
 * year costs and a `computed` block (termLengthMonths, monthlyRent,
 * annualRent, rentPerSqft, totalLifetimeCost, daysToExpiry).
 *
 * Returns `null` when the location has no active lease (server 404),
 * which the LeaseSummaryTab uses to render its empty state without
 * surfacing an error banner.
 *
 * @param {string} locationId
 * @returns {Promise<object|null>}
 * @throws {NetworkError | AuthError | ValidationError}
 */
export async function getActiveLeaseSummary(locationId) {
  assertValidLocId("getActiveLeaseSummary", locationId);
  try {
    return await jsonRequest(
      `/api/locations/${encodeURIComponent(locationId)}/lease`,
      { method: "GET" }
    );
  } catch (err) {
    if (err instanceof NotFoundError) return null;
    throw err;
  }
}

/**
 * LSE-LEASE-TAB-AREAS-AND-SWITCHER — fetch all non-deleted leases for a
 * location, each with a nested `areas` array from lease_areas. Used by the
 * multi-lease switcher strip. No computed financial values.
 *
 * Returns an empty array when the location exists but has no leases.
 *
 * @param {string} locationId
 * @returns {Promise<object[]>}
 * @throws {NetworkError | AuthError | ValidationError | NotFoundError}
 */
export async function listLocationLeases(locationId) {
  assertValidLocId("listLocationLeases", locationId);
  return jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases`,
    { method: "GET" }
  );
}

/**
 * Fetch recovery records explicitly attached to one selected head lease.
 * No location-wide or lease-type-based association is performed.
 */
export async function listLeaseRecoveries(locationId, leaseId) {
  assertValidLocId("listLeaseRecoveries", locationId);
  assertValidLeaseId("listLeaseRecoveries", leaseId);
  return jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/recoveries`,
    { method: "GET" }
  );
}

// ─── Writes ───────────────────────────────────────────────────────────────

/**
 * @param {string} locationId  REGION-CITYCODE-SEQ
 * @param {object} payload     NewLease; status/type/startDate/endDate/currency required.
 * @returns {Promise<object>}  Created Lease.
 * @throws  {NetworkError | AuthError | ValidationError | ConflictError | NotFoundError}
 * Audit:   lease.create
 *
 * On success: invalidates the full locationCache (so the parent location's
 * lease tree refetch sees the new row) BEFORE recordAudit, matching the
 * locations.js write pattern.
 */
export async function addLease(locationId, payload) {
  assertValidLocId("addLease", locationId);
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new ValidationError("addLease: payload object required");
  }
  // LSE-LEASE-TAB-DRAFT-CREATION: draft leases only require status; the four
  // "operational required" fields (type/startDate/endDate/currency) are optional
  // so the user can save partial data at any point.
  const isDraft = payload.status === "draft";
  const requiredFields = isDraft ? ["status"] : REQUIRED_NEW;
  for (const f of requiredFields) {
    if (payload[f] == null || payload[f] === "") {
      throw new ValidationError(`addLease: missing required field "${f}"`, { field: f });
    }
  }
  assertEnums("addLease", payload);

  const created = await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases`,
    { method: "POST", body: JSON.stringify(payload) }
  );
  invalidateAll();
  recordAudit({
    action:     "lease.create",
    entityType: "lease",
    entityId:   created.id,
    before:     null,
    after:      created,
  });
  return created;
}

/**
 * @param {string} locationId
 * @param {string} leaseId  L.<locId>.<seq>
 * @param {object} patch    Partial<Lease>; shallow-merged on the server.
 * @returns {Promise<object>}  Updated Lease.
 * @throws  {NetworkError | AuthError | ValidationError | NotFoundError | ConflictError}
 * Audit:   lease.update (with before/after when caller supplies before)
 *
 * The caller passes `before` so the audit row carries a meaningful
 * snapshot — the location cache stores the parent location, not
 * individual leases, so we can't reconstruct it here.
 */
export async function updateLeaseArea(locationId, leaseId, areaId, patch) {
  assertValidLocId("updateLeaseArea", locationId);
  assertValidLeaseId("updateLeaseArea", leaseId);
  if (typeof areaId !== "string" || !areaId.trim()) throw new ValidationError("Area ID required");
  const error = areaFootagePatchError(patch);
  if (error) throw new ValidationError(error);
  const response = await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/areas/${encodeURIComponent(areaId)}`,
    { method: "PATCH", body: JSON.stringify(patch) },
  );
  invalidateAll();
  // Only the server emits the authoritative area audit, in the write transaction.
  return response.data;
}

export async function updateLease(locationId, leaseId, patch, { before = null } = {}) {
  assertValidLocId("updateLease",   locationId);
  assertValidLeaseId("updateLease", leaseId);
  if (!patch || typeof patch !== "object" || Array.isArray(patch)) {
    throw new ValidationError("updateLease: patch object required");
  }
  if ("id" in patch || "locationId" in patch) {
    throw new ValidationError("updateLease: cannot mutate id or locationId", { field: "id" in patch ? "id" : "locationId" });
  }
  assertEnums("updateLease", patch);

  const after = await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}`,
    { method: "PATCH", body: JSON.stringify(patch) }
  );
  invalidateAll();
  if (
    Object.prototype.hasOwnProperty.call(patch, "aiSummary")
    || Object.prototype.hasOwnProperty.call(patch, "aiSummarySource")
    || Object.prototype.hasOwnProperty.call(patch, "aiSummaryAt")
  ) {
    emitCommandIndexStale();
  }
  recordAudit({
    action:     "lease.update",
    entityType: "lease",
    entityId:   leaseId,
    before,
    after,
  });
  return after;
}

// ─── LEASE-SUMMARY-AI-S1 ─────────────────────────────────────────────────
//
// POST /api/locations/:locId/leases/:leaseId/generate-summary
// Triggers LLM generation server-side. Returns { aiSummary, aiSummaryAt, aiSummarySource }.
// Throws NetworkError / AuthError on transport failures; a 500 from the server
// (LLM unavailable) is re-thrown as-is so callers can distinguish it from auth/network errors.

/**
 * @param {string} locationId
 * @param {string} leaseId
 * @returns {Promise<{ aiSummary: string, aiSummaryAt: string, aiSummarySource: 'ai' }>}
 */
export async function generateLeaseSummary(locationId, leaseId) {
  assertValidLocId("generateLeaseSummary",   locationId);
  assertValidLeaseId("generateLeaseSummary", leaseId);
  const result = await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/generate-summary`,
    { method: "POST" }
  );
  invalidateAll();
  emitCommandIndexStale();
  return result;
}

// ─── Allowances + Draws (slice 3b) ────────────────────────────────────────
//
// Endpoints (server/routes/leases.js):
//   POST   /api/locations/:locId/leases/:leaseId/allowances
//   PATCH  /api/locations/:locId/leases/:leaseId/allowances/:allowanceId
//   DELETE /api/locations/:locId/leases/:leaseId/allowances/:allowanceId
//   POST   /api/locations/:locId/leases/:leaseId/allowances/:allowanceId/draws
//
// Per CONTRACT.md (Allowances & Draws):
//   - Server strips drawnToDate from POST/PATCH; canonical drawn total is
//     SUM(allowance_draws.amount). We never send it from the client.
//   - 409 on POST/PATCH allowance: should not happen on the happy path
//     (no unique constraints) but surfaced if the server adds one later.
//   - 409 on POST draw: over-draw OR draw on an expired allowance
//     (server checks expiry_date > now()).
//   - 409 on DELETE allowance: rows still exist in allowance_draws and
//     the FK blocks the parent delete (also caught as 23503 → 409 in the
//     route's race handler — slice 3a fix). Hard delete; id is reused.
//
// Audit actions: allowance.create / allowance.update / allowance.delete / draw.create.
// Same write pattern as leases: invalidateAll() → recordAudit() AFTER
// success only; never audit on a thrown error.

/**
 * LSE9: Fetch all allowances for a lease, each with nested draws array.
 * Mirrors listCriticalDates — no cache; called only when the Concessions
 * section is visible (LeaseSummaryTab on mount / refetch).
 *
 * @param {string} locationId
 * @param {string} leaseId
 * @returns {Promise<Array>}  Allowances in expiry_date ASC order, each
 *                            with a `draws: []` sub-array.
 */
export async function listAllowances(locationId, leaseId) {
  assertValidLocId("listAllowances",   locationId);
  assertValidLeaseId("listAllowances", leaseId);
  return jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/allowances`,
    { method: "GET" }
  );
}

/**
 * @param {string} locationId
 * @param {string} leaseId
 * @param {object} payload  type/totalAmount/currency required.
 *                          drawnToDate is stripped server-side; do not send.
 * @returns {Promise<object>}  Created Allowance.
 * Audit: allowance.create
 */
export async function addAllowance(locationId, leaseId, payload) {
  assertValidLocId("addAllowance",   locationId);
  assertValidLeaseId("addAllowance", leaseId);
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new ValidationError("addAllowance: payload object required");
  }
  for (const f of REQUIRED_NEW_ALLOWANCE) {
    if (payload[f] == null || payload[f] === "") {
      throw new ValidationError(`addAllowance: missing required field "${f}"`, { field: f });
    }
  }
  assertAllowanceEnums("addAllowance", payload);

  const created = await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/allowances`,
    { method: "POST", body: JSON.stringify(payload) }
  );
  invalidateAll();
  recordAudit({
    action:     "allowance.create",
    entityType: "allowance",
    entityId:   created.id,
    before:     null,
    after:      created,
  });
  return created;
}

/**
 * @param {string} locationId
 * @param {string} leaseId
 * @param {string} allowanceId
 * @param {object} patch        Shallow-merged on the server. drawnToDate is
 *                              stripped server-side; do not send.
 * @returns {Promise<object>}  Updated Allowance.
 * Audit: allowance.update (with caller-supplied before snapshot)
 */
export async function updateAllowance(locationId, leaseId, allowanceId, patch, { before = null } = {}) {
  assertValidLocId("updateAllowance",       locationId);
  assertValidLeaseId("updateAllowance",     leaseId);
  assertValidAllowanceId("updateAllowance", allowanceId);
  if (!patch || typeof patch !== "object" || Array.isArray(patch)) {
    throw new ValidationError("updateAllowance: patch object required");
  }
  if ("id" in patch || "leaseId" in patch) {
    throw new ValidationError("updateAllowance: cannot mutate id or leaseId", {
      field: "id" in patch ? "id" : "leaseId",
    });
  }
  assertAllowanceEnums("updateAllowance", patch);

  const after = await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/allowances/${encodeURIComponent(allowanceId)}`,
    { method: "PATCH", body: JSON.stringify(patch) }
  );
  invalidateAll();
  recordAudit({
    action:     "allowance.update",
    entityType: "allowance",
    entityId:   allowanceId,
    before,
    after,
  });
  return after;
}

/**
 * @param {string} locationId
 * @param {string} leaseId
 * @param {string} allowanceId
 * @returns {Promise<null>}
 * @throws  {ConflictError} 409 when allowance still has draws (FK block,
 *          including the 23503 → 409 race remap in the route).
 * Audit:   allowance.delete (with caller-supplied before snapshot)
 *
 * Hard delete; the id may be reused on a future POST.
 */
export async function deleteAllowance(locationId, leaseId, allowanceId, { before = null } = {}) {
  assertValidLocId("deleteAllowance",       locationId);
  assertValidLeaseId("deleteAllowance",     leaseId);
  assertValidAllowanceId("deleteAllowance", allowanceId);

  await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/allowances/${encodeURIComponent(allowanceId)}`,
    { method: "DELETE" }
  );
  invalidateAll();
  recordAudit({
    action:     "allowance.delete",
    entityType: "allowance",
    entityId:   allowanceId,
    before,
    after:      null,
  });
  return null;
}

/**
 * @param {string} locationId
 * @param {string} leaseId
 * @param {string} allowanceId
 * @param {object} payload      amount + drawDate required. description optional.
 * @returns {Promise<object>}  Created Draw row (server returns the new draw,
 *                             not the parent allowance).
 * @throws  {ConflictError} 409 on over-draw OR on draw against an expired
 *          allowance (expiry_date <= now()). The thrown ConflictError's
 *          message carries the server's distinguishing wording — the
 *          AllowancePanel renders three different banners off it.
 * Audit:   draw.create
 */
export async function recordDraw(locationId, leaseId, allowanceId, payload) {
  assertValidLocId("recordDraw",       locationId);
  assertValidLeaseId("recordDraw",     leaseId);
  assertValidAllowanceId("recordDraw", allowanceId);
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new ValidationError("recordDraw: payload object required");
  }
  for (const f of REQUIRED_NEW_DRAW) {
    if (payload[f] == null || payload[f] === "") {
      throw new ValidationError(`recordDraw: missing required field "${f}"`, { field: f });
    }
  }

  const created = await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/allowances/${encodeURIComponent(allowanceId)}/draws`,
    { method: "POST", body: JSON.stringify(payload) }
  );
  invalidateAll();
  recordAudit({
    action:     "draw.create",
    entityType: "draw",
    entityId:   created.id,
    before:     null,
    after:      { ...created, allowanceId },
  });
  return created;
}

/**
 * @param {string} locationId
 * @param {string} leaseId
 * @param {string} allowanceId
 * @param {string} drawId       DRW.<allowanceId>.<seq3>
 * @param {object} patch        Partial draw: drawDate, amount, description.
 *                              At least one field must be present.
 * @param {object} [opts]
 * @param {object} [opts.before]  Caller-supplied draw snapshot for the audit row.
 * @returns {Promise<object>}   Updated draw row.
 * @throws  {ValidationError} 400 when amount is not a positive number.
 * @throws  {ConflictError}   409 when edited amount would exceed the allowance cap.
 * @throws  {NotFoundError}   404 when the draw does not exist.
 * Audit:   draw.update (server-side)
 */
export async function patchDraw(locationId, leaseId, allowanceId, drawId, patch, { before = null } = {}) {
  assertValidLocId("patchDraw",       locationId);
  assertValidLeaseId("patchDraw",     leaseId);
  assertValidAllowanceId("patchDraw", allowanceId);
  assertValidDrawId("patchDraw",      drawId);
  if (!patch || typeof patch !== "object" || Array.isArray(patch)) {
    throw new ValidationError("patchDraw: patch object required");
  }
  if ("id" in patch || "allowanceId" in patch) {
    throw new ValidationError("patchDraw: cannot mutate id or allowanceId", {
      field: "id" in patch ? "id" : "allowanceId",
    });
  }
  if (patch.amount !== undefined) {
    const amt = parseFloat(patch.amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      throw new ValidationError("patchDraw: amount must be a positive number", {
        field: "amount", got: patch.amount,
      });
    }
  }

  const after = await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/allowances/${encodeURIComponent(allowanceId)}/draws/${encodeURIComponent(drawId)}`,
    { method: "PATCH", body: JSON.stringify(patch) }
  );
  invalidateAll();
  recordAudit({
    action:     "draw.update",
    entityType: "draw",
    entityId:   drawId,
    before,
    after:      { ...after, allowanceId },
  });
  return after;
}

/**
 * @param {string} locationId
 * @param {string} leaseId
 * @param {string} allowanceId
 * @param {string} drawId       DRW.<allowanceId>.<seq3>
 * @param {object} [opts]
 * @param {object} [opts.before]  Caller-supplied draw snapshot for the audit row.
 * @returns {Promise<null>}
 * @throws  {NotFoundError} 404 when the draw does not exist.
 * Audit:   draw.delete
 */
export async function deleteDraw(locationId, leaseId, allowanceId, drawId, { before = null } = {}) {
  assertValidLocId("deleteDraw",       locationId);
  assertValidLeaseId("deleteDraw",     leaseId);
  assertValidAllowanceId("deleteDraw", allowanceId);
  assertValidDrawId("deleteDraw",      drawId);

  await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/allowances/${encodeURIComponent(allowanceId)}/draws/${encodeURIComponent(drawId)}`,
    { method: "DELETE" }
  );
  invalidateAll();
  recordAudit({
    action:     "draw.delete",
    entityType: "draw",
    entityId:   drawId,
    before,
    after:      null,
  });
  return null;
}

// ─── Critical dates + watch items (slice 4b) ─────────────────────────────
//
// Endpoints (server/routes/leases.js):
//   GET    /api/locations/:locId/leases/:leaseId/critical-dates
//   POST   /api/locations/:locId/leases/:leaseId/critical-dates
//   PATCH  /api/locations/:locId/leases/:leaseId/critical-dates/:cdId
//   DELETE /api/locations/:locId/leases/:leaseId/critical-dates/:cdId
//   GET    /api/locations/:locId/leases/:leaseId/watch-items
//   POST   /api/locations/:locId/leases/:leaseId/watch-items
//   PATCH  /api/locations/:locId/leases/:leaseId/watch-items/:itemId
//   DELETE /api/locations/:locId/leases/:leaseId/watch-items/:itemId
//
// Both entities use server-generated uuids (Postgres `gen_random_uuid()`).
// `:cdId` / `:itemId` are validated against UUID_RE so a malformed id 400s
// at the route boundary instead of leaking pg's 22P02. We mirror that on
// the client so the pre-flight gate fails fast without a round-trip.
//
// Audit actions: critical_date.create / .update / .delete and
// watch_item.create / .update / .delete. The exception is the open|carry
// → resolved transition on watch_item.update, which emits the distinct
// action `watch_item.resolve` (matching the server-side audit shape
// documented in CONTRACT.md). The transition detector keys off the
// caller-supplied `before` snapshot so re-resolving an already-resolved
// item is treated as a generic `.update` (matching server behavior, which
// does not re-stamp `resolved_at` in that case).

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const CRITICAL_DATE_TYPES = new Set([
  "renewal_notice", "break_option", "expiration", "escalation",
  "deposit_burndown", "allowance_deadline", "allowance_warning",
  "allowance_expiry", "cam_audit", "other",
]);
const REQUIRED_NEW_CRITICAL_DATE = ["type", "dueDate"];

const WATCH_ITEM_TYPES      = new Set(["risk", "action", "milestone", "negotiation_intel"]);
const WATCH_ITEM_SEVERITIES = new Set(["high", "medium", "low"]);
const WATCH_ITEM_STATUSES   = new Set(["open", "resolved", "carry_to_renewal"]);
const REQUIRED_NEW_WATCH_ITEM = ["type", "severity", "title"];

function assertValidUuid(method, field, id) {
  if (!id || typeof id !== "string" || !UUID_RE.test(id)) {
    throw new ValidationError(
      `${method}: invalid ${field} "${id}" - must be a uuid`,
      { field, got: id }
    );
  }
}

function assertCriticalDateEnums(method, body) {
  if (body.type != null && !CRITICAL_DATE_TYPES.has(body.type)) {
    throw new ValidationError(
      `${method}: invalid critical date type "${body.type}"`,
      { field: "type", got: body.type, allowed: [...CRITICAL_DATE_TYPES] }
    );
  }
}

function assertWatchItemEnums(method, body) {
  if (body.type != null && !WATCH_ITEM_TYPES.has(body.type)) {
    throw new ValidationError(
      `${method}: invalid watch item type "${body.type}"`,
      { field: "type", got: body.type, allowed: [...WATCH_ITEM_TYPES] }
    );
  }
  if (body.severity != null && !WATCH_ITEM_SEVERITIES.has(body.severity)) {
    throw new ValidationError(
      `${method}: invalid watch item severity "${body.severity}"`,
      { field: "severity", got: body.severity, allowed: [...WATCH_ITEM_SEVERITIES] }
    );
  }
  if (body.status != null && !WATCH_ITEM_STATUSES.has(body.status)) {
    throw new ValidationError(
      `${method}: invalid watch item status "${body.status}"`,
      { field: "status", got: body.status, allowed: [...WATCH_ITEM_STATUSES] }
    );
  }
}

// ─── Critical dates ───────────────────────────────────────────────────────

/**
 * @param {string} locationId
 * @param {string} leaseId
 * @returns {Promise<object[]>}  CriticalDate rows ordered by dueDate ASC, id ASC.
 *
 * Read-only; no cache and no audit (per CONTRACT.md). The lease intelligence
 * panel uses this to render its per-lease list independently of the parent
 * location refetch — keeps the UI snappy when adding many dates in a row.
 */
export async function listCriticalDates(locationId, leaseId) {
  assertValidLocId("listCriticalDates",   locationId);
  assertValidLeaseId("listCriticalDates", leaseId);
  return jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/critical-dates`,
    { method: "GET" }
  );
}

/**
 * @param {string} locationId
 * @param {string} leaseId
 * @param {object} payload  type + dueDate required; description optional.
 * @returns {Promise<object>}  Created CriticalDate.
 * Audit: critical_date.create
 */
export async function addCriticalDate(locationId, leaseId, payload) {
  assertValidLocId("addCriticalDate",   locationId);
  assertValidLeaseId("addCriticalDate", leaseId);
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new ValidationError("addCriticalDate: payload object required");
  }
  // Strict pre-flight rejects on server-managed identity fields. Without
  // these the server would silently drop them via its write allowlist
  // (`CD_WRITE_FIELDS`), which masks caller bugs and would let stale UI
  // state leak past the validator. Same gate the route enforces for
  // `id`; we extend it to `leaseId` for symmetry with the PATCH path.
  if ("id" in payload) {
    throw new ValidationError("addCriticalDate: cannot supply id; server assigns uuid", { field: "id" });
  }
  if ("leaseId" in payload) {
    throw new ValidationError("addCriticalDate: cannot supply leaseId in body; it comes from the URL", { field: "leaseId" });
  }
  for (const f of REQUIRED_NEW_CRITICAL_DATE) {
    if (payload[f] == null || payload[f] === "") {
      throw new ValidationError(`addCriticalDate: missing required field "${f}"`, { field: f });
    }
  }
  assertCriticalDateEnums("addCriticalDate", payload);

  const created = await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/critical-dates`,
    { method: "POST", body: JSON.stringify(payload) }
  );
  invalidateAll();
  recordAudit({
    action:     "critical_date.create",
    entityType: "critical_date",
    entityId:   created.id,
    before:     null,
    after:      created,
  });
  return created;
}

/**
 * @param {string} locationId
 * @param {string} leaseId
 * @param {string} cdId       Postgres-generated uuid.
 * @param {object} patch      Shallow-merged on the server. id/leaseId rejected.
 * @returns {Promise<object>}  Updated CriticalDate.
 * Audit: critical_date.update (with caller-supplied before snapshot)
 */
export async function updateCriticalDate(locationId, leaseId, cdId, patch, { before = null } = {}) {
  assertValidLocId("updateCriticalDate",      locationId);
  assertValidLeaseId("updateCriticalDate",    leaseId);
  assertValidUuid("updateCriticalDate", "cdId", cdId);
  if (!patch || typeof patch !== "object" || Array.isArray(patch)) {
    throw new ValidationError("updateCriticalDate: patch object required");
  }
  if ("id" in patch || "leaseId" in patch) {
    throw new ValidationError("updateCriticalDate: cannot mutate id or leaseId", {
      field: "id" in patch ? "id" : "leaseId",
    });
  }
  assertCriticalDateEnums("updateCriticalDate", patch);

  const after = await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/critical-dates/${encodeURIComponent(cdId)}`,
    { method: "PATCH", body: JSON.stringify(patch) }
  );
  invalidateAll();
  recordAudit({
    action:     "critical_date.update",
    entityType: "critical_date",
    entityId:   cdId,
    before,
    after,
  });
  return after;
}

/**
 * @param {string} locationId
 * @param {string} leaseId
 * @param {string} cdId
 * @returns {Promise<null>}
 * Audit: critical_date.delete (with caller-supplied before snapshot)
 *
 * Hard delete; uuid is reused only by Postgres' generator (collision
 * probability negligible).
 */
export async function deleteCriticalDate(locationId, leaseId, cdId, { before = null } = {}) {
  assertValidLocId("deleteCriticalDate",      locationId);
  assertValidLeaseId("deleteCriticalDate",    leaseId);
  assertValidUuid("deleteCriticalDate", "cdId", cdId);

  await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/critical-dates/${encodeURIComponent(cdId)}`,
    { method: "DELETE" }
  );
  invalidateAll();
  recordAudit({
    action:     "critical_date.delete",
    entityType: "critical_date",
    entityId:   cdId,
    before,
    after:      null,
  });
  return null;
}

// ─── Watch items ──────────────────────────────────────────────────────────

/**
 * @param {string} locationId
 * @param {string} leaseId
 * @returns {Promise<object[]>}  WatchItem rows; server orders open-first
 *                               then most-recently-created.
 *
 * Read-only; no cache and no audit. The Intelligence Ledger panel applies
 * its own severity sort within each status group (and pivots on a sticky
 * filter pill), so it accepts the server's open-first ordering as-is.
 */
export async function listWatchItems(locationId, leaseId) {
  assertValidLocId("listWatchItems",   locationId);
  assertValidLeaseId("listWatchItems", leaseId);
  return jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/watch-items`,
    { method: "GET" }
  );
}

/**
 * @param {string} locationId
 * @param {string} leaseId
 * @returns {Promise<object[]>}  Lease documents ordered by execution_date desc,
 *                                 nulls last. CamelCase fields.
 */
export async function listDocuments(locationId, leaseId) {
  assertValidLocId("listDocuments",   locationId);
  assertValidLeaseId("listDocuments", leaseId);
  return jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/documents`,
    { method: "GET" }
  );
}

/**
 * @param {string} locationId
 * @param {string} leaseId
 * @param {object} payload  type + severity + title required; body optional.
 *                          status defaults to "open" server-side.
 *                          createdBy / resolvedAt are server-stamped and rejected here.
 * @returns {Promise<object>}  Created WatchItem.
 * Audit: watch_item.create
 */
export async function addWatchItem(locationId, leaseId, payload) {
  assertValidLocId("addWatchItem",   locationId);
  assertValidLeaseId("addWatchItem", leaseId);
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new ValidationError("addWatchItem: payload object required");
  }
  // Strict pre-flight rejects on server-managed / URL-derived fields.
  // Same rationale as addCriticalDate — the server's write allowlist
  // would silently drop these but we'd rather fail loudly so caller
  // bugs surface immediately.
  if ("id" in payload || "leaseId" in payload || "createdBy" in payload || "resolvedAt" in payload) {
    const field = "id" in payload ? "id"
                : "leaseId" in payload ? "leaseId"
                : "createdBy" in payload ? "createdBy"
                : "resolvedAt";
    throw new ValidationError(
      `addWatchItem: cannot supply ${field} (server-managed or URL-derived)`,
      { field }
    );
  }
  for (const f of REQUIRED_NEW_WATCH_ITEM) {
    if (payload[f] == null || payload[f] === "") {
      throw new ValidationError(`addWatchItem: missing required field "${f}"`, { field: f });
    }
  }
  assertWatchItemEnums("addWatchItem", payload);

  const created = await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/watch-items`,
    { method: "POST", body: JSON.stringify(payload) }
  );
  invalidateAll();
  recordAudit({
    action:     "watch_item.create",
    entityType: "watch_item",
    entityId:   created.id,
    before:     null,
    after:      created,
  });
  return created;
}

/**
 * @param {string} locationId
 * @param {string} leaseId
 * @param {string} itemId
 * @param {object} patch    Shallow-merged. id / leaseId / createdBy / resolvedAt rejected.
 * @param {object} [opts]
 * @param {object} [opts.before]  Snapshot used to (a) populate the audit
 *                                row and (b) detect the open|carry → resolved
 *                                transition so the audit action switches to
 *                                `watch_item.resolve`.
 * @returns {Promise<object>}  Updated WatchItem.
 * Audit: watch_item.update — OR watch_item.resolve when transitioning to
 *        resolved from a non-resolved before-state. Re-patching an already-
 *        resolved item (incl. with status:"resolved" again) emits .update,
 *        matching the server's behavior of not re-stamping `resolved_at`.
 */
export async function updateWatchItem(locationId, leaseId, itemId, patch, { before = null } = {}) {
  assertValidLocId("updateWatchItem",        locationId);
  assertValidLeaseId("updateWatchItem",      leaseId);
  assertValidUuid("updateWatchItem", "itemId", itemId);
  if (!patch || typeof patch !== "object" || Array.isArray(patch)) {
    throw new ValidationError("updateWatchItem: patch object required");
  }
  if ("id" in patch || "leaseId" in patch || "createdBy" in patch || "resolvedAt" in patch) {
    const field = "id" in patch ? "id"
                : "leaseId" in patch ? "leaseId"
                : "createdBy" in patch ? "createdBy"
                : "resolvedAt";
    throw new ValidationError(
      `updateWatchItem: cannot mutate ${field} (server-managed)`,
      { field }
    );
  }
  assertWatchItemEnums("updateWatchItem", patch);

  const after = await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/watch-items/${encodeURIComponent(itemId)}`,
    { method: "PATCH", body: JSON.stringify(patch) }
  );
  invalidateAll();

  // Mirror the server's audit-action switch: open|carry → resolved emits
  // `.resolve`; everything else (incl. resolved → resolved no-op and
  // resolved → open reopen) emits `.update`. Keying off `before` rather
  // than just `patch.status` is what gives us the idempotent re-resolve.
  const transitioningToResolved =
    patch.status === "resolved" && before != null && before.status !== "resolved";
  const action = transitioningToResolved ? "watch_item.resolve" : "watch_item.update";

  recordAudit({
    action,
    entityType: "watch_item",
    entityId:   itemId,
    before,
    after,
  });
  return after;
}

/**
 * @param {string} locationId
 * @param {string} leaseId
 * @param {string} itemId
 * @returns {Promise<null>}
 * Audit: watch_item.delete (with caller-supplied before snapshot)
 *
 * Hard delete; admin-only on the server.
 */
export async function deleteWatchItem(locationId, leaseId, itemId, { before = null } = {}) {
  assertValidLocId("deleteWatchItem",        locationId);
  assertValidLeaseId("deleteWatchItem",      leaseId);
  assertValidUuid("deleteWatchItem", "itemId", itemId);

  await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/watch-items/${encodeURIComponent(itemId)}`,
    { method: "DELETE" }
  );
  invalidateAll();
  recordAudit({
    action:     "watch_item.delete",
    entityType: "watch_item",
    entityId:   itemId,
    before,
    after:      null,
  });
  return null;
}

// ─── Year-cost CRUD  (LSE-UNIFIED-EDIT-UX-PHASE-2 + EDIT-LEASE-MODAL-AUDIT) ──

/**
 * POST a new year-cost row for a lease.
 * @param {string} locationId
 * @param {string} leaseId
 * @param {{ year: number, rent?: number|null, opex?: number|null, capex?: number|null, totalCost?: number|null }} row
 * Audit: year_cost.create
 */
export async function addYearCost(locationId, leaseId, row) {
  assertValidLocId("addYearCost", locationId);
  assertValidLeaseId("addYearCost", leaseId);
  if (row == null || typeof row !== "object") {
    throw new ValidationError("addYearCost: row object required");
  }
  const data = await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/year-costs`,
    { method: "POST", body: JSON.stringify(row) }
  );
  const created = data?.data ?? null;
  invalidateAll();
  if (created) {
    recordAudit({
      action:     "year_cost.create",
      entityType: "year_cost",
      entityId:   String(created.id),
      before:     null,
      after:      { ...created, locationId, leaseId },
    });
  }
  return created;
}

/**
 * PATCH rent/opex/capex on an existing year-cost row.
 * At least one of rent/opex/capex must be supplied.
 * Server recomputes total_cost = rent + opex + capex.
 * @param {string} locationId
 * @param {string} leaseId
 * @param {string|number} ycId  row id
 * @param {{ rent?: number|null, opex?: number|null, capex?: number|null, abatement?: number|null }} patch
 * @param {{ before?: object|null }} [opts]
 * @returns {Promise<object>}  Updated year-cost row.
 * Audit: year_cost.update
 */
export async function updateYearCost(locationId, leaseId, ycId, patch, { before = null } = {}) {
  assertValidLocId("updateYearCost", locationId);
  assertValidLeaseId("updateYearCost", leaseId);
  if (ycId == null) {
    throw new ValidationError("updateYearCost: ycId required");
  }
  if (!patch || typeof patch !== "object" || Array.isArray(patch)) {
    throw new ValidationError("updateYearCost: patch object required");
  }
  if (patch.rent === undefined && patch.opex === undefined && patch.capex === undefined && patch.abatement === undefined) {
    throw new ValidationError("updateYearCost: patch must include at least one of rent, opex, capex, abatement");
  }
  const data = await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/year-costs/${encodeURIComponent(String(ycId))}`,
    { method: "PATCH", body: JSON.stringify(patch) }
  );
  const after = data?.data ?? null;
  invalidateAll();
  recordAudit({
    action:     "year_cost.update",
    entityType: "year_cost",
    entityId:   String(ycId),
    before,
    after:      after ? { ...after, locationId, leaseId } : null,
  });
  return after;
}

/**
 * DELETE a year-cost row by its id.
 * @param {string} locationId
 * @param {string} leaseId
 * @param {string|number} ycId  row id as returned by GET year-costs
 * @param {{ before?: object|null }} [opts]
 * Audit: year_cost.delete
 */
export async function deleteYearCost(locationId, leaseId, ycId, { before = null } = {}) {
  assertValidLocId("deleteYearCost", locationId);
  assertValidLeaseId("deleteYearCost", leaseId);
  if (ycId == null) {
    throw new ValidationError("deleteYearCost: ycId required");
  }
  await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/year-costs/${encodeURIComponent(String(ycId))}`,
    { method: "DELETE" }
  );
  invalidateAll();
  recordAudit({
    action:     "year_cost.delete",
    entityType: "year_cost",
    entityId:   String(ycId),
    before,
    after:      null,
  });
}

// ─── LEASE-DELETE-MANAGE-AFFORDANCE ────────────────────────────────────────
//
// deleteDraftLease — hard-deletes a DRAFT lease (server 409s if not draft).
// voidLease        — admin-only soft-delete (deleted_at) for non-draft
//                     leases; requires a non-blank reason. Server 400s if
//                     reason is missing/blank, 409 if the lease is a draft.
// Both invalidate the location cache and audit-log the action, mirroring
// the delete*/update* patterns above.

/**
 * @param {string} locationId
 * @param {string} leaseId
 * @param {object} [opts]
 * @param {object} [opts.before]  Prior lease snapshot, for the audit entry.
 * @returns {Promise<null>}
 * @throws  {ConflictError} 409 if the lease is not a draft.
 */
export async function deleteDraftLease(locationId, leaseId, { before = null } = {}) {
  assertValidLocId("deleteDraftLease",   locationId);
  assertValidLeaseId("deleteDraftLease", leaseId);

  await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}`,
    { method: "DELETE" }
  );
  invalidateAll();
  recordAudit({
    action:     "lease.delete",
    entityType: "lease",
    entityId:   leaseId,
    before,
    after:      null,
  });
  return null;
}

/**
 * @param {string} locationId
 * @param {string} leaseId
 * @param {string} reason         Required, non-blank.
 * @param {object} [opts]
 * @param {object} [opts.before]  Prior lease snapshot, for the audit entry.
 * @returns {Promise<object>}     Updated lease row (deleted_at/voided_at/void_reason set).
 * @throws  {ValidationError} if reason is blank.
 * @throws  {ConflictError}   409 if the lease is a draft (use deleteDraftLease instead).
 */
export async function voidLease(locationId, leaseId, reason, { before = null } = {}) {
  assertValidLocId("voidLease",   locationId);
  assertValidLeaseId("voidLease", leaseId);
  if (typeof reason !== "string" || !reason.trim()) {
    throw new ValidationError("voidLease: a non-blank reason is required", { field: "reason" });
  }

  const after = await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/leases/${encodeURIComponent(leaseId)}/void`,
    { method: "POST", body: JSON.stringify({ reason: reason.trim() }) }
  );
  invalidateAll();
  recordAudit({
    action:     "lease.void",
    entityType: "lease",
    entityId:   leaseId,
    before,
    after,
  });
  return after;
}

// Re-export error classes so callers can `instanceof`-discriminate without
// pulling from a second module.
export {
  AuthError,
  ConflictError,
  NetworkError,
  NotFoundError,
  ValidationError,
};

// Test-only exports for unit tests (vitest reads these).
export const __test = {
  CRITICAL_DATE_TYPES,
  WATCH_ITEM_TYPES,
  WATCH_ITEM_SEVERITIES,
  WATCH_ITEM_STATUSES,
  UUID_RE,
};
